import React from 'react';
import { useLocation } from '@reach/router';
import { GatsbyImage, IGatsbyImageData } from 'gatsby-plugin-image';
import styled from 'styled-components';
import { useCountry } from '@hooks/useCountry';
import { replaceText } from '@utils/handleTextReplacement';

interface Props {
  icons: {
    icon: {
      gatsbyImageData: IGatsbyImageData;
      alt: string;
      url: string;
    };
    icon_text: { text: string };
    icon_description?: { text: string };
  }[];
  isVerticalDirection?: boolean;
  style?: string;
  background?: string;
  iconSize?: string;
}

const HeaderIcons: React.FC<Props> = ({ icons, isVerticalDirection = false, style = 'icon-left', background = '', iconSize }) => {
  const location = useLocation();
  const { country } = useCountry();
  const minImageSize = iconSize || '35px';

  if (!icons.length) {
    return null;
  }

  return (
    <IconsRowWrap $isVerticalDirection={isVerticalDirection} className="icon-rows-wrap">
      {icons.map((icon, index) => (
        <IconWrap key={index} className={`icon-wrap style-${style}`} $isVerticalDirection={isVerticalDirection} $background={background}>
          <GatsbyImage
            image={icon.icon.gatsbyImageData}
            alt={icon.icon.alt ?? ''}
            objectFit="contain"
            style={{ minWidth: minImageSize, minHeight: minImageSize }}
          />
          <ColumnWrap>
            <IconTextWrap
              $isVerticalDirection={isVerticalDirection}
              className="icon-text"
              dangerouslySetInnerHTML={{ __html: replaceText(icon.icon_text.text, country, location) }}
            />
            <IconDescriptionWrap>{icon.icon_description?.text}</IconDescriptionWrap>
          </ColumnWrap>
        </IconWrap>
      ))}
    </IconsRowWrap>
  );
};

export default HeaderIcons;

interface StyleProps {
  $isVerticalDirection?: boolean;
  $background?: string;
}

const IconsRowWrap = styled.div<StyleProps>`
  display: flex;
  justify-content: flex-start;
  margin-bottom: 0;
  column-gap: 12px;
  flex-direction: ${({ $isVerticalDirection }: StyleProps) => ($isVerticalDirection ? 'column' : 'row')};
  gap: ${({ $isVerticalDirection }: StyleProps) => ($isVerticalDirection ? '20px' : '')};

  @media (max-width: 1024px) {
    justify-content: space-around;
    width: 100%;
    padding: 0 30px;
  }
`;

const IconWrap = styled.div<StyleProps>`
  display: flex;
  align-items: center;
  column-gap: 10px;
  gap: ${({ $isVerticalDirection }: StyleProps) => ($isVerticalDirection ? '20px' : '10px')};
  background-color: ${({ $background }: StyleProps) => $background || 'transparent'};
  padding: ${({ $background }: StyleProps) => ($background ? '10px' : '0')};
  border-radius: 10px;

  &.style-icon-top {
    flex-direction: column;
    align-items: flex-start;
    row-gap: 10px;
    text-align: left;

    @media (max-width: 1024px) {
      align-items: center;
    }
  }

  @media (max-width: 1024px) {
    flex-direction: ${({ $isVerticalDirection }: StyleProps) => ($isVerticalDirection ? 'row' : 'column')};
    max-width: ${({ $isVerticalDirection }: StyleProps) => ($isVerticalDirection ? '100%' : '110px')};
    justify-content: start;
  }
`;

const IconTextWrap = styled.div<StyleProps>`
  align-self: ${({ $isVerticalDirection }: StyleProps) => ($isVerticalDirection ? 'start' : 'center')};
  font-family: BrownStd;
  font-size: ${({ $isVerticalDirection }: StyleProps) => ($isVerticalDirection ? '13px' : '12px')};
  font-style: normal;
  font-weight: 700;
  line-height: ${({ $isVerticalDirection }: StyleProps) => ($isVerticalDirection ? '18px' : '16px')};
  letter-spacing: 0.02em;
  text-align: left;
  text-transform: uppercase;
  width: ${({ $isVerticalDirection }: StyleProps) => ($isVerticalDirection ? 'auto' : '100%')};
  -webkit-font-smoothing: antialiased;

  @media (max-width: 1024px) {
    text-align: center;
    margin-top: 10px;
    margin-left: 0;
  }
`;

const IconDescriptionWrap = styled.div`
  font-family: BrownStd;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 140%;

  &:empty {
    display: none;
  }

  @media (max-width: 1024px) {
    text-align: left;
  }
`;

const ColumnWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;
